import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { RouteInstance, RouteParams } from 'atomic-router';
import { Link } from 'atomic-router-react';
import { StoreValue } from 'effector';
import { FileSearch } from 'lucide-react';
import { forwardRef, Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Cashdesk } from 'shared/api';
import { translated } from 'shared/lib';
import { accountRoute, cashdeskRoute, userRoute } from 'shared/routing';
import { createRegistry, MultiSelect, RegistryRootProps, SearchInput } from 'shared/ui';
import { CashdeskRegistryModel } from '../../model';
import cls from './cashdesk-registry.module.scss';

export type CashdeskRegistryProps = RegistryRootProps & {
	withTitle?: boolean;
	withAccount?: boolean;
	emptyActionSlot?: ReactNode;
	topBarActionSlot?: ReactNode;
	tableTdActionSlot?: (cashdesk: Cashdesk) => ReactNode;
};

export const createCashdeskRegistry = ({ model, currencies }: CashdeskRegistryModel) => {
	const Registry = createRegistry(model);

	const NameFilter = reflect({
		view: SearchInput<'input'>,
		bind: {
			id: 'cashdesk-registry-name-filter',
			label: translated('cashdesk-registry.filters.name.label'),
			placeholder: translated('cashdesk-registry.filters.name.placeholder'),
			className: cls.cashdeskRegistryNameFilter,
			value: model.filters.name.$value,
			onChange: (e) => model.filters.name.setValue(e.target.value),
		},
	});

	const CurrenciesFilter = reflect({
		view: MultiSelect,
		bind: {
			id: 'cashdesk-registry-currencies-filter',
			label: translated('cashdesk-registry.filters.currencies.label'),
			placeholder: translated('cashdesk-registry.filters.currencies.placeholder'),
			className: cls.cashdeskRegistryCurrenciesFilter,
			data: currencies,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
			searchable: true,
		},
	});

	const CashdeskRegistry = forwardRef<HTMLDivElement, CashdeskRegistryProps>(
		(
			{
				withTitle = true,
				withAccount = true,
				emptyActionSlot,
				topBarActionSlot,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('cashdesk-registry.title')}</Registry.Title>}
					<Registry.TopBar className={cls.cashdeskRegistryTopBar}>
						<Registry.Filters className={cls.cashdeskRegistryFilters}>
							<NameFilter />
							<CurrenciesFilter />
						</Registry.Filters>
						<div className={cls.cashdeskRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							{withAccount && <Registry.Table.Col />}
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								<Registry.Table.ThSorter field='name'>
									{t('cashdesk-registry.table-columns.name')}
								</Registry.Table.ThSorter>
								{withAccount && (
									<Registry.Table.ThSorter field='account'>
										{t('cashdesk-registry.table-columns.account')}
									</Registry.Table.ThSorter>
								)}
								<Registry.Table.ThSorter field='currency'>
									{t('cashdesk-registry.table-columns.currency')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='balance'>
									{t('cashdesk-registry.table-columns.balance')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='cashiers'>
									{t('cashdesk-registry.table-columns.cashiers')}
								</Registry.Table.ThSorter>
								<Registry.Table.ThSorter field='note'>
									{t('cashdesk-registry.table-columns.note')}
								</Registry.Table.ThSorter>
								<Registry.Table.Th>
									{t('cashdesk-registry.table-columns.actions')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList
								getProps={(row) => ({
									variant: row.isLocked ? 'locked' : 'default',
								})}
							>
								{(row) => (
									<>
										<Registry.Table.Td>
											<Anchor
												data-test-id={`cashdesk-registry-link-${row.id}`}
												component={Link<StoreValue<typeof cashdeskRoute.$params>>}
												to={cashdeskRoute}
												params={{ id: row.id }}
											>
												{row.name}
											</Anchor>
										</Registry.Table.Td>
										{withAccount && (
											<Registry.Table.Td>
												<Anchor
													component={Link}
													to={accountRoute}
													query={{ id: row.account.id }}
												>
													{row.account.name}
												</Anchor>
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.balance} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											{row.cashiers.map((cashier, index) => (
												<Fragment key={cashier.id}>
													<Anchor
														data-test-id={`cashdesk-registry-cashier-link-${cashier.id}`}
														to={userRoute as unknown as RouteInstance<RouteParams>}
														params={
															{
																id: cashier.id,
															} satisfies StoreValue<typeof userRoute.$params>
														}
														component={Link}
													>
														{cashier.name}
													</Anchor>
													{index === row.cashiers.length - 1 ? null : (
														<span className={cls.cashdeskRegistryComa}>, </span>
													)}
												</Fragment>
											))}
										</Registry.Table.Td>
										<Registry.Table.Td>{row.note}</Registry.Table.Td>
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('cashdesk-registry.empty.title')}
						message={t('cashdesk-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('cashdesk-registry.table-empty.title')}
						message={t('cashdesk-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return CashdeskRegistry;
};
